import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { ArrowLeft, Eye, Send, EyeOff, CalendarClock, SlidersHorizontal, Loader2, Check, AlertCircle } from "lucide-react";
import { http, formatError } from "@/lib/api";
import { StatusBadge, ukDateTime, postAction } from "./blogApi";
import { CtaBlock } from "./CtaNode";
import { EditorToolbar } from "./EditorToolbar";
import { SeoPanel } from "./SeoPanel";
import { ScheduleDialog } from "./BlogList";

const FIELDS = ["title", "slug", "excerpt", "body_html", "category", "author", "published_at", "image", "image_alt", "meta_title", "meta_description",
  "focus_keyword", "canonical", "og_title", "og_image", "cta_heading", "cta_note", "cta_label", "cta_to"];
const toForm = (p) => { const m = { ...p, ...(p.draft || {}) }; return Object.fromEntries(FIELDS.map((k) => [k, m[k] ?? ""])); };

const SaveState = ({ state }) => {
  const map = { saved: [Check, "Saved", "text-emerald-700"], saving: [Loader2, "Saving", "text-slate-500"], dirty: [Loader2, "Unsaved changes", "text-slate-500"], error: [AlertCircle, "Not saved", "text-red-600"] };
  const [I, label, cls] = map[state] || map.saved;
  return <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${cls}`} data-testid="editor-save-state"><I className={`h-3.5 w-3.5 ${state === "saving" ? "animate-spin" : ""}`} />{label}</span>;
};

export const BlogEditor = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [form, setForm] = useState(null);
  const [save, setSave] = useState("saved");
  const [saveErr, setSaveErr] = useState("");
  const [seo, setSeo] = useState(false);
  const [sched, setSched] = useState(false);
  const [cats, setCats] = useState([]);
  const formRef = useRef(null);
  const dirty = useRef(false);

  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, code: false, codeBlock: false, link: { openOnClick: false, autolink: true, HTMLAttributes: { target: null, rel: null } } }),
      Image.configure({ inline: false }), TableKit.configure({ table: { resizable: false } }), CtaBlock],
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: "prose-dad ProseMirror", "data-testid": "editor-body" } },
    onUpdate: ({ editor: ed }) => update("body_html", ed.getHTML()),
  });

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([http.get(`/admin/posts/${id}`), http.get("/admin/posts/categories")]);
    setPost(r.data); setCats(c.data);
    const f = toForm(r.data);
    formRef.current = f; setForm(f); dirty.current = false; setSave("saved");
    return f;
  }, [id]);

  useEffect(() => {
    load().then((f) => editor?.commands.setContent(f.body_html || "", { emitUpdate: false })).catch((e) => toast.error(formatError(e)));
  }, [load, editor]);

  const update = (k, v) => {
    formRef.current = { ...formRef.current, [k]: v };
    setForm(formRef.current);
    dirty.current = true;
    setSave("dirty");
  };

  const flush = useCallback(async () => {
    if (!dirty.current) return true;
    dirty.current = false;
    setSave("saving");
    try {
      await http.put(`/admin/posts/${id}/autosave`, formRef.current);
      setSave(dirty.current ? "dirty" : "saved"); setSaveErr("");
      return true;
    } catch (e) {
      dirty.current = true; setSave("error"); setSaveErr(formatError(e));
      return false;
    }
  }, [id]);

  useEffect(() => {
    if (save !== "dirty") return undefined;
    const t = setTimeout(flush, 2500);
    return () => clearTimeout(t);
  }, [form, save, flush]);

  const live = post?.status === "published";
  const run = async (action) => {
    if (!(await flush())) { toast.error("Please fix the save error first."); return; }
    try {
      const r = await postAction(id, action);
      await load();
      const msg = { publish: live ? "Updated. The live article now shows your changes." : "Published.", unpublish: "Unpublished. The article is now a draft." }[action];
      toast.success(msg, r.status === "published" ? { action: { label: "View article", onClick: () => window.open(`/blog/${r.slug}`, "_blank") } } : undefined);
    } catch (e) { toast.error(formatError(e)); }
  };
  const preview = async () => { await flush(); window.open(`/admin/preview/${id}`, "_blank"); };

  if (!post || !form) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#7C3AED]" /></div>;
  return (
    <div className="min-h-screen bg-[#F5F0FF]" data-testid="blog-editor">
      <header className="sticky top-0 z-30 border-b border-violet-100 bg-[#13052A] px-4 py-3 text-white sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3">
          <Link to="/admin" onClick={flush} className="inline-flex items-center gap-1.5 text-sm font-bold text-violet-100 hover:text-white" data-testid="editor-back"><ArrowLeft className="h-4 w-4" /> All articles</Link>
          <StatusBadge status={post.status} testid="editor-status" />
          {post.status === "scheduled" && <span className="text-xs text-violet-200">Goes live {ukDateTime(post.publish_at)} UK time</span>}
          <span className="rounded-full bg-white px-2.5 py-1"><SaveState state={save} /></span>
          {live && <span className="text-xs text-violet-200" data-testid="editor-live-note">Live article: your edits stay private until you press Update.</span>}
          <div className="ml-auto flex flex-wrap gap-2">
            <button onClick={() => setSeo(true)} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20" data-testid="editor-open-seo"><SlidersHorizontal className="h-4 w-4" /> SEO</button>
            <button onClick={preview} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20" data-testid="editor-preview"><Eye className="h-4 w-4" /> Preview</button>
            {live ? <>
              <button onClick={() => run("unpublish")} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20" data-testid="editor-unpublish"><EyeOff className="h-4 w-4" /> Unpublish</button>
              <button onClick={() => run("publish")} className="btn-accent !py-2" data-testid="editor-update"><Send className="h-4 w-4" /> Update</button>
            </> : <>
              <button onClick={async () => { if (await flush()) setSched(true); }} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20" data-testid="editor-schedule"><CalendarClock className="h-4 w-4" /> Schedule</button>
              <button onClick={() => run("publish")} className="btn-accent !py-2" data-testid="editor-publish"><Send className="h-4 w-4" /> Publish now</button>
            </>}
          </div>
        </div>
        {saveErr && <p className="mx-auto mt-2 max-w-6xl text-sm font-semibold text-red-300" data-testid="editor-save-error">{saveErr}</p>}
      </header>
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-8 sm:py-12">
        <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_30px_60px_-40px_rgba(46,16,101,.4)]">
          <div className="px-6 pt-8 sm:px-12 sm:pt-12">
            <textarea value={form.title} onChange={(e) => update("title", e.target.value)} rows={2} placeholder="Article title"
              className="w-full resize-none border-0 text-3xl font-extrabold leading-tight text-[#13052A] outline-none sm:text-4xl" data-testid="editor-title" aria-label="Article title (H1)" />
            <textarea value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} rows={2} placeholder="Short introduction shown under the title and on the blog page"
              className="mt-2 w-full resize-none border-0 text-base text-slate-600 outline-none" data-testid="editor-excerpt" aria-label="Introduction" />
          </div>
          <div className="dad-editor mt-4 border-t border-violet-100">
            <EditorToolbar editor={editor} />
            <div className="px-6 py-8 sm:px-12"><EditorContent editor={editor} /></div>
          </div>
        </div>
      </main>
      <SeoPanel open={seo} onOpenChange={setSeo} form={form} set={update} categories={cats} />
      <ScheduleDialog post={sched ? post : null} onClose={() => setSched(false)} onDone={() => load()} />
    </div>
  );
};
